/** @module @liquidefy/code-editor/syntax/syntax-grammar @version 2.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  GrammarId,
  LanguageId,
  GrammarDefinition,
  GrammarPattern,
  GrammarRepository,
  GrammarCaptures,
  ScopeResolution,
  Token,
  TokenizerState,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asGrammarId,
  now,
} from '../code-editor.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 LOCAL TYPES — grammar engine state, rules, registry
// ═══════════════════════════════════════════════════════════════════════════════

/** A compiled grammar rule with resolved references. */
export interface GrammarRule {
  readonly id: string;
  readonly name?: string;
  readonly contentName?: string;
  readonly pattern: GrammarPattern;
  readonly matchRegex?: RegExp;
  readonly beginRegex?: RegExp;
  readonly endRegex?: RegExp;
}

/** Scope entry pushed onto the grammar stack when entering a begin/end rule. */
export interface GrammarScope {
  readonly scopeName: string;
  readonly endRegex?: RegExp;
  readonly contentName?: string;
}

/** A token produced by the grammar engine with scope ancestry. */
export interface TokenScope {
  readonly startOffset: number;
  readonly endOffset: number;
  readonly scopes: string[];
}

/**
 * Running state for a TextMate-style grammar engine applied to a document.
 * The stack tracks nested grammar scopes (e.g. embedded languages).
 */
export interface GrammarState {
  readonly grammar: GrammarDefinition;
  readonly scopeStack: GrammarScope[];
  readonly ruleStack: GrammarRule[];
  readonly lineStates: Map<number, TokenizerState>;
}

/** A registry mapping language IDs to their grammar definitions. */
export interface GrammarRegistry {
  readonly grammars: Map<LanguageId, GrammarDefinition>;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 GRAMMAR CREATION — parse and initialize grammars
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Create a new GrammarState from a GrammarDefinition.
 * The returned state has an empty scope/rule stack — ready to begin tokenizing
 * the document from line 0.
 *
 * @param definition The compiled grammar definition (scopeName, patterns, repository).
 */
export function createGrammar(definition: GrammarDefinition): EditorResult<GrammarState> {
  if (!definition.scopeName) {
    return err('INVALID_GRAMMAR', 'Grammar definition must have a scopeName');
  }
  if (!definition.patterns || definition.patterns.length === 0) {
    return err('INVALID_GRAMMAR', 'Grammar definition must have at least one top-level pattern');
  }

  const rootScope: GrammarScope = {
    scopeName: definition.scopeName,
  };

  const state: GrammarState = {
    grammar: definition,
    scopeStack: [rootScope],
    ruleStack: [],
    lineStates: new Map(),
  };

  return ok(state, 'GRAMMAR_CREATED', `Grammar created: ${definition.scopeName}`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §4 SCOPE RESOLUTION — map tokens to scopes
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Resolve the TextMate scope for a given token, searching the grammar's scope
 * definitions and the current rule stack.
 *
 * @param grammar The grammar state to search.
 * @param token   The token whose scope should be resolved.
 * @returns A ScopeResolution containing the scope chain and detected token type.
 */
export function resolveScope(grammar: GrammarState, token: Token): ScopeResolution {
  // Build scope chain from stack + token's own scopes
  const stackScopes = grammar.scopeStack.map(s => s.scopeName);
  const combined = [...stackScopes, ...token.scopes];
  return {
    scopes: combined,
    tokenType: token.type,
  };
}

/**
 * Find the best-matching grammar rule for the content at a given position on a line.
 * Walks all top-level patterns plus patterns from the active grammar scope in the stack.
 *
 * @param grammar  Current grammar state.
 * @param line     Full text of the line being tokenized.
 * @param position Column offset within the line (character index, 0-based).
 * @returns The matched rule or null when no rule matches at the position.
 */
export function matchRule(
  grammar: GrammarState,
  line: string,
  position: number,
): GrammarRule | null {
  const remaining = line.slice(position);
  const patterns = grammar.grammar.patterns;

  return findFirstMatchingPattern(patterns, grammar.grammar.repository, remaining);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 GRAMMAR STACK — nested grammars (e.g. embedded JS in HTML)
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Push a grammar onto the scope stack, entering a nested language context.
 * This is used when an embedded language block begins (e.g. `<script>` in HTML).
 *
 * @param state   Current grammar state.
 * @param grammar The grammar definition for the embedded language.
 */
export function pushGrammarStack(
  state: GrammarState,
  grammar: GrammarDefinition,
): EditorResult<GrammarState> {
  const newScope: GrammarScope = {
    scopeName: grammar.scopeName,
  };
  return ok(
    { ...state, scopeStack: [...state.scopeStack, newScope] },
    'GRAMMAR_PUSHED',
    `Entered grammar scope: ${grammar.scopeName}`,
  );
}

/**
 * Pop the top grammar from the scope stack, exiting a nested language context.
 * Returns an error if the stack would become empty (cannot pop the root grammar).
 *
 * @param state Current grammar state.
 */
export function popGrammarStack(state: GrammarState): EditorResult<GrammarState> {
  if (state.scopeStack.length <= 1) {
    return err('GRAMMAR_STACK_UNDERFLOW', 'Cannot pop the root grammar scope');
  }
  const scopeStack = state.scopeStack.slice(0, -1);
  return ok(
    { ...state, scopeStack },
    'GRAMMAR_POPPED',
    'Exited grammar scope',
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 SCOPE ANCESTRY — full scope chain at a position
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Get the complete scope ancestry at a character position in the document.
 * Returns scopes from outermost (root grammar) to innermost (current nesting).
 *
 * @param state    Current grammar state.
 * @param position The position (line + column) to query.
 */
export function getScopeChain(
  state: GrammarState,
  position: { readonly line: number; readonly column: number },
): string[] {
  // The scope chain is the full stack of scope names at this position
  // (stored in the grammar scope stack, enriched by line state if available)
  const lineState = state.lineStates.get(position.line);
  const stackScopes = state.scopeStack.map(s => s.scopeName);

  if (lineState) {
    return [...stackScopes, ...lineState.ruleStack];
  }
  return stackScopes;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §7 TEXTMATE LOADING — parse JSON/plist grammar format
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Load a GrammarDefinition from a TextMate JSON grammar object.
 * Accepts the raw parsed JSON from a `.tmLanguage.json` or `.tmGrammar.json` file.
 * Unknown fields are silently ignored for forward compatibility.
 *
 * @param json The parsed JSON object from a TextMate grammar file.
 */
export function loadGrammarFromTextMate(json: Record<string, unknown>): EditorResult<GrammarDefinition> {
  const scopeName = json['scopeName'];
  if (typeof scopeName !== 'string' || !scopeName) {
    return err('INVALID_TEXTMATE', 'TextMate grammar must have a string scopeName');
  }

  const rawPatterns = json['patterns'];
  if (!Array.isArray(rawPatterns)) {
    return err('INVALID_TEXTMATE', 'TextMate grammar must have a patterns array');
  }

  const patterns: GrammarPattern[] = rawPatterns.map(p => parsePattern(p as Record<string, unknown>));

  const rawRepo = json['repository'];
  const repository: Record<string, GrammarPattern> = {};
  if (rawRepo && typeof rawRepo === 'object') {
    for (const [key, value] of Object.entries(rawRepo as Record<string, unknown>)) {
      repository[key] = parsePattern(value as Record<string, unknown>);
    }
  }

  const definition: GrammarDefinition = {
    id: asGrammarId(generateId('gram')),
    scopeName,
    patterns,
    repository,
  };

  return ok(definition, 'GRAMMAR_LOADED', `Loaded TextMate grammar: ${scopeName}`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §8 GRAMMAR MERGING — extension and override
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Merge two grammar definitions, with the override taking precedence.
 * Override patterns are prepended (higher priority) and the repositories are merged
 * with override entries winning on key conflicts.
 *
 * @param base     The base grammar to extend.
 * @param override The override grammar whose patterns and rules take precedence.
 */
export function mergeGrammars(
  base: GrammarDefinition,
  override: GrammarDefinition,
): EditorResult<GrammarDefinition> {
  const mergedRepository: GrammarRepository = {
    ...base.repository,
    ...override.repository,
  };

  const merged: GrammarDefinition = {
    id: asGrammarId(generateId('gram')),
    scopeName: override.scopeName || base.scopeName,
    patterns: [...override.patterns, ...base.patterns],
    repository: mergedRepository,
  };

  return ok(merged, 'GRAMMARS_MERGED', `Merged grammars: ${base.scopeName} + ${override.scopeName}`);
}

// ═══════════════════════════════════════════════════════════════════════════════
// §9 REGISTRY LOOKUP — find grammar by language ID
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Look up a grammar definition from a registry by language ID.
 * Returns an error if the language does not have a registered grammar.
 *
 * @param registry   The grammar registry to search.
 * @param languageId The language whose grammar is requested.
 */
export function getGrammarForLanguage(
  registry: GrammarRegistry,
  languageId: LanguageId,
): EditorResult<GrammarDefinition> {
  const grammar = registry.grammars.get(languageId);
  if (!grammar) {
    return err('GRAMMAR_NOT_FOUND', `No grammar registered for language: ${languageId}`);
  }
  return ok(grammar, 'GRAMMAR_FOUND', `Found grammar for: ${languageId}`);
}

/**
 * Register a grammar for a language ID in the registry, returning the updated registry.
 *
 * @param registry   Current grammar registry.
 * @param languageId The language this grammar covers.
 * @param grammar    The grammar definition to register.
 */
export function registerGrammar(
  registry: GrammarRegistry,
  languageId: LanguageId,
  grammar: GrammarDefinition,
): GrammarRegistry {
  const grammars = new Map(registry.grammars);
  grammars.set(languageId, grammar);
  return { grammars };
}

/**
 * Create an empty grammar registry.
 */
export function createGrammarRegistry(): GrammarRegistry {
  return { grammars: new Map() };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §10 INTERNAL HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

/** Parse a raw JSON object into a GrammarPattern. */
function parsePattern(raw: Record<string, unknown>): GrammarPattern {
  const pattern: GrammarPattern = {
    match: typeof raw['match'] === 'string' ? raw['match'] : undefined,
    begin: typeof raw['begin'] === 'string' ? raw['begin'] : undefined,
    end: typeof raw['end'] === 'string' ? raw['end'] : undefined,
    name: typeof raw['name'] === 'string' ? raw['name'] : undefined,
    contentName: typeof raw['contentName'] === 'string' ? raw['contentName'] : undefined,
    captures: raw['captures'] ? parseCaptures(raw['captures'] as Record<string, unknown>) : undefined,
    beginCaptures: raw['beginCaptures'] ? parseCaptures(raw['beginCaptures'] as Record<string, unknown>) : undefined,
    endCaptures: raw['endCaptures'] ? parseCaptures(raw['endCaptures'] as Record<string, unknown>) : undefined,
    patterns: Array.isArray(raw['patterns'])
      ? (raw['patterns'] as Record<string, unknown>[]).map(p => parsePattern(p))
      : undefined,
  };
  return pattern;
}

/** Parse capture group definitions from a TextMate grammar JSON object. */
function parseCaptures(raw: Record<string, unknown>): GrammarCaptures {
  const captures: Record<string, { readonly name: string }> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (value && typeof value === 'object' && 'name' in value && typeof (value as Record<string, unknown>)['name'] === 'string') {
      captures[key] = { name: (value as Record<string, unknown>)['name'] as string };
    }
  }
  return captures;
}

/**
 * Walk patterns (and their repository references) to find the first pattern
 * that matches at the start of `text`. Returns a GrammarRule or null.
 */
function findFirstMatchingPattern(
  patterns: readonly GrammarPattern[],
  repository: GrammarRepository,
  text: string,
): GrammarRule | null {
  for (const pattern of patterns) {
    // Handle include references
    const includeRef = (pattern as Record<string, unknown>)['include'];
    if (typeof includeRef === 'string') {
      const refKey = includeRef.startsWith('#') ? includeRef.slice(1) : null;
      const refPattern = refKey ? repository[refKey] : undefined;
      if (refKey && refPattern) {
        const result = findFirstMatchingPattern([refPattern], repository, text);
        if (result) return result;
      }
      continue;
    }

    if (pattern.match) {
      try {
        const re = new RegExp(pattern.match);
        if (re.test(text)) {
          return { id: generateId('rule'), name: pattern.name, pattern, matchRegex: re };
        }
      } catch {
        // Invalid regex — skip
      }
    } else if (pattern.begin) {
      try {
        const re = new RegExp(pattern.begin);
        if (re.test(text)) {
          const endRe = pattern.end ? safeRegex(pattern.end) : undefined;
          return { id: generateId('rule'), name: pattern.name, pattern, beginRegex: re, endRegex: endRe };
        }
      } catch {
        // Invalid regex — skip
      }
    } else if (pattern.patterns) {
      const result = findFirstMatchingPattern(pattern.patterns, repository, text);
      if (result) return result;
    }
  }
  return null;
}

/** Compile a regex pattern string, returning undefined on failure. */
function safeRegex(pattern: string): RegExp | undefined {
  try {
    return new RegExp(pattern);
  } catch {
    return undefined;
  }
}
